// <copyright file="quote-fabrication-icons.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { css, CSSResult, html, TemplateResult } from 'lit';
import { type FabricationBucket, tallyFabricationBuckets } from './quote-fabrication-buckets';
import { getApiFactory } from '../../../api/api-injector';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';
import { QuoteFabricationCountView } from '../../../api/dealer-api-interface-all';
import { userDataStore } from '../../common/current-user-data-store';

/**
 * Styles for the fabrication icon chips, injected into a <webmodule-table>'s shadow root via its
 * customStyles hook - cell content renders there, out of reach of the page stylesheet. Shared so
 * the quote list and the project view's quote table size the chips the same way.
 */
export const quoteFabricationIconsStyles: CSSResult = css`
  /* Fabrication state icons sit on their own line under the status badge, one small chip per state
     the quote's items are spread across. Counts live in the tooltip, so each chip is icon-only and
     sized down to hold the row height. */
  .quote-fabrication-icons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.2rem;
    margin-top: 0.2rem;
  }

  .quote-fabrication-icons .badge {
    padding: 0.15rem 0.3rem;
    line-height: 1;
  }
`;

/**
 * The fabrication states a quote's items are spread across, as a row of icon-only chips under the
 * status badge, with the counts in a hover tooltip. One chip per state actually present, so a
 * fully delivered quote shows a single tick while one split between ordering and production shows
 * two; nothing renders when the quote has no fabrication. Shared so the quote list and the project
 * view read identically.
 */
export function renderQuoteFabricationIcons(buckets: FabricationBucket[]): TemplateResult {
  const present = buckets.filter(bucket => bucket.count > 0);
  if (present.length === 0) return html``;

  // With one state holding every item there is nothing to compare against, so the count adds
  // nothing the single icon has not already said; name the chip by the state alone.
  const only = present.length === 1;
  // One resolver for the chip's accessible name and its tooltip line, so the two can never disagree.
  const stateText = (bucket: FabricationBucket): string =>
    only ? bucket.label : LangFranchiseeQuoteListView.fabricationItemsInState(bucket.count, bucket.label);

  // Tooltip lines go through the content SLOT, one block element each: the `content` attribute
  // renders as a plain text node under white-space: normal and would collapse every line onto one.
  return html`<webmodule-tooltip placement="right" hoist>
    <div slot="content">
      <div>${LangFranchiseeQuoteListView.fabricationTooltipHeading()}</div>
      ${present.map(
        bucket =>
          html`<div>
            <webmodule-icon library="fa" name=${bucket.icon} aria-hidden="true"></webmodule-icon>
            <span>${stateText(bucket)}</span>
          </div>`
      )}
    </div>
    <span class="quote-fabrication-icons">
      ${present.map(
        bucket =>
          // Icon-only chip named for a screen reader (and a native mouse tooltip); the glyph is decorative.
          html`<span
            class="badge ${bucket.badgeClass}"
            role="img"
            aria-label=${stateText(bucket)}
            title=${stateText(bucket)}
            ><webmodule-icon library="fa" name=${bucket.icon} aria-hidden="true"></webmodule-icon
          ></span>`
      )}
    </span>
  </webmodule-tooltip>`;
}

/**
 * Groups a flat list of per-quote fabrication counts into tallied buckets, indexed by quote id.
 * Pure, so a caller that has already fetched the counts (for a project-wide total, say) can derive
 * the per-quote buckets from the same response rather than fetching again.
 */
export function bucketsByQuote(counts: QuoteFabricationCountView[]): Map<string, FabricationBucket[]> {
  const countsByQuote = new Map<string, QuoteFabricationCountView[]>();
  for (const count of counts) {
    const existing = countsByQuote.get(count.quoteId);
    if (existing) existing.push(count);
    else countsByQuote.set(count.quoteId, [count]);
  }

  const byQuote = new Map<string, FabricationBucket[]>();
  for (const [quoteId, quoteCounts] of countsByQuote) {
    byQuote.set(quoteId, tallyFabricationBuckets(quoteCounts));
  }
  return byQuote;
}

/**
 * The raw per-quote fabrication counts for a page of quotes, in one call. The server pre-aggregates,
 * so this is a handful of rows however many items sit behind them. Empty outside fabricator mode,
 * for no quotes, or when the call fails - so a caller can tally without its own guards. Return the
 * counts rather than buckets so a caller that needs both a project-wide total and the per-quote
 * split can derive both from one response.
 */
export async function fetchQuoteFabricationCounts(quoteIds: string[]): Promise<QuoteFabricationCountView[]> {
  // Empty on any failure, per the contract above. isFabricatorMode throws until the user store is
  // loaded, and this runs inside the quote list's Promise.all over prefetches - an uncaught throw
  // there aborts the whole page's row load rather than just dropping the fabrication icons.
  try {
    if (!userDataStore.isFabricatorMode || quoteIds.length === 0) return [];

    const result = await getApiFactory().fabricationJob().getQuoteFabricationSummaries({ quoteIds });
    return result?.counts ?? [];
  } catch {
    return [];
  }
}

/**
 * Fabrication buckets for a page of quotes, indexed by quote id - the fetch grouped in one step,
 * for a caller that only needs the per-quote split. Quotes with no fabrication get no entry.
 */
export async function loadQuoteFabricationBuckets(quoteIds: string[]): Promise<Map<string, FabricationBucket[]>> {
  return bucketsByQuote(await fetchQuoteFabricationCounts(quoteIds));
}
